import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Camera, RotateCcw, UserRound, CheckCircle2 } from "lucide-react";

export function AttendanceDialog({ open, onOpenChange }) {
  const [emps, setEmps] = useState([]);
  const [sel, setSel] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const videoRef = useRef(null);
  const streamRef = useRef(null);

  const stopCam = () => { try { streamRef.current?.getTracks().forEach((t) => t.stop()); } catch (e) {} streamRef.current = null; };

  useEffect(() => {
    if (!open) { stopCam(); setSel(null); setResult(null); return; }
    api.get("/employees").then((r) => setEmps(r.data)).catch(() => {});
  }, [open]);

  useEffect(() => {
    let cancelled = false;
    const start = async () => {
      try {
        const s = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "user" }, audio: false });
        if (cancelled) { s.getTracks().forEach((t) => t.stop()); return; }
        streamRef.current = s;
        if (videoRef.current) { videoRef.current.srcObject = s; await videoRef.current.play().catch(() => {}); }
      } catch (e) { toast.error("Tidak bisa mengakses kamera. Izinkan akses kamera di browser."); }
    };
    if (sel && !result) start();
    return () => { cancelled = true; };
  }, [sel, result]);

  const capture = async () => {
    const v = videoRef.current; if (!v) return;
    const c = document.createElement("canvas");
    c.width = v.videoWidth || 480; c.height = v.videoHeight || 360;
    c.getContext("2d").drawImage(v, 0, 0, c.width, c.height);
    const photo = c.toDataURL("image/jpeg", 0.6);
    setBusy(true);
    try {
      const { data } = await api.post("/attendance", { employee_id: sel.id, photo });
      stopCam();
      setResult(data);
      toast.success(`${data.employee_name} absen ${data.status.toUpperCase()} · ${data.time}`);
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) stopCam(); onOpenChange(o); }}>
      <DialogContent className="glass border-neutral-200 text-neutral-900 max-w-md">
        <DialogHeader><DialogTitle className="font-display flex items-center gap-2"><Camera size={18} className="text-crimson" /> Absensi Karyawan</DialogTitle></DialogHeader>
        {result ? (
          <div className="flex flex-col items-center gap-3 py-4 text-center" data-testid="attendance-result">
            <CheckCircle2 size={54} className="text-emerald-500" />
            <div className="text-lg font-bold">{result.employee_name}</div>
            <div className={`rounded-full px-4 py-1 text-sm font-bold ${result.status === "masuk" ? "bg-emerald-500/15 text-emerald-600" : "bg-crimson/10 text-crimson"}`}>Absen {result.status === "masuk" ? "MASUK" : "PULANG"} · {result.time}</div>
            <Button data-testid="attendance-done-btn" onClick={() => onOpenChange(false)} className="mt-2 bg-crimson font-bold hover:bg-red-500">Selesai</Button>
          </div>
        ) : !sel ? (
          <div>
            <p className="mb-2 text-xs text-muted-foreground">Pilih nama Anda untuk absen. Absen pertama hari ini = Masuk, absen kedua = Pulang.</p>
            <div className="grid max-h-72 grid-cols-2 gap-2 overflow-y-auto" data-testid="attendance-employee-list">
              {emps.map((e) => (
                <button key={e.id} data-testid={`att-emp-${e.id}`} onClick={() => setSel(e)} className="flex flex-col items-start rounded-xl bg-neutral-100 px-3 py-2 text-left hover:bg-neutral-200">
                  <span className="font-semibold text-neutral-900">{e.name}</span>
                  <span className="text-[11px] text-muted-foreground">{e.position || "-"}</span>
                </button>
              ))}
              {emps.length === 0 && <p className="col-span-2 text-sm text-muted-foreground">Belum ada karyawan. Tambahkan di dashboard Owner/Manager.</p>}
            </div>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-3">
            <div className="flex items-center gap-1 text-sm font-bold text-neutral-900"><UserRound size={14} /> {sel.name}</div>
            <div className="overflow-hidden rounded-2xl bg-black" style={{ width: "100%", aspectRatio: "4 / 3" }}>
              <video ref={videoRef} playsInline muted className="h-full w-full object-cover" style={{ transform: "scaleX(-1)" }} />
            </div>
            <div className="flex w-full gap-2">
              <Button data-testid="att-back-btn" onClick={() => { stopCam(); setSel(null); }} variant="outline" className="flex-1 border-neutral-300 font-bold text-neutral-900"><RotateCcw size={16} className="mr-1" /> Ganti</Button>
              <Button data-testid="att-capture-btn" disabled={busy} onClick={capture} className="flex-1 bg-crimson font-bold hover:bg-red-500"><Camera size={16} className="mr-1" /> {busy ? "Menyimpan…" : "Ambil Foto & Absen"}</Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
